import React from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import DialogContentText from '@mui/material/DialogContentText'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Snackbar from '@mui/material/Snackbar'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'

import AffiliateTracking from './AffiliateTracking'

import { useHistory, useParams } from 'react-router-dom'
import { useQuery } from 'react-query'

import { FirebaseContext } from '../../utils/firebase'
import 'firebase/auth'

import { API_ROOT_URL } from '../../constants'
import * as ROUTES from '../../constants/routes'
import { generatePostbackParams } from '../../utils/helpers'
import { TUNE } from '../../constants/platforms'

import tuneLogoImg from '../../media/tune_logo.png'

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    left: 66,
    right: 0,
    width: 'calc(100% - 66px)',
    height: '100vh',
    overflowY: 'auto',
    backgroundColor: theme.palette.grey[50],
  },
  logoImg: {
    width: 100,
  },
  syncButton: {
    width: 110,
  },
  redButton: {
    color: theme.palette.error.main,
    '&:hover': {
      background: 'rgba(255,23,68,0.04)',
    },
  },
}))

const REVOKE_SUCCESS_MESSAGE = 'Account removed. Data will no longer be synced from this account. Redirecting back to settings...'
const WEBHOOK_URL_BASE = `https://us-central1-ltv-numbers-${process.env.REACT_APP_ENV === 'production' ? 'prod' : 'dev'}.cloudfunctions.net/webhooks/tune/`

const TuneSource = () => {
  const classes = useStyles()
  const { id } = useParams()
  const history = useHistory()
  const firebase = React.useContext(FirebaseContext)

  const [message, setMessage] = React.useState('')
  const [showMessage, setShowMessage] = React.useState(false)
  const [messageSeverity ] = React.useState('success')
  const [showRevokeConfirmation, setShowRevokeConfirmation] = React.useState(false)


  const {
    isLoading: isLoadingIntegration,
    data: integration = {}
  } = useQuery(['integration-get', id], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      }).then(res => res.json())
    }),
    {
      cacheTime: 15 * 60 * 1000,  // 15 minutes
      staleTime: 15 * 60 * 1000,  // 15 minutes
      refetchOnWindowFocus: false,
    }
  )

  const { isLoading: isLoadingRevoke, refetch: revoke } = useQuery(['revoke-tune'], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations/tune/revoke`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          integration_id: id
        })
      }).then(res => res.json())
    }),
    {
      enabled: false,
      staleTime: Infinity,
      refetchOnReconnect: false,
      onSuccess: () => {
        setShowRevokeConfirmation(false)
        setMessage(REVOKE_SUCCESS_MESSAGE)
        setShowMessage(true)
        setTimeout(() => history.push(ROUTES.SETTINGS), 3000)
      },
    }
  )

  const {
    isLoading: isLoadingConnectors,
    data: connectors = [],
  } = useQuery(['connectors-get', id], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/connectors`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      }).then(res => res.json())
    }),
    {
      cacheTime: 0,
      staleTime: 0,
      refetchOnWindowFocus: false,
    }
  )

  const handleCloseMessage = (_, reason) => {
    if (reason === 'clickaway') return
    setShowMessage(false)
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
  }

  return (
    <div className={classes.root}>
      <Box padding={2}>
        <Button
          variant='outlined'
          onClick={() => history.push(ROUTES.SETTINGS)}
          size='small'
        >
          Back
        </Button>
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={2}>
                  <img
                    className={classes.logoImg}
                    alt='tune_logo'
                    src={tuneLogoImg}
                  />
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='h5' gutterBottom>
                    Tune Source
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Account ID */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Account ID
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box height='100%' display='flex' alignItems='center'>
                    <Typography variant='body2'>
                      {isLoadingIntegration ? <Skeleton variant='text' width={100} /> : `${integration.platform_id}`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Name */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Name
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box height='100%' display='flex' alignItems='center'>
                    <Typography variant='body2'>
                      {isLoadingIntegration ? <Skeleton variant='text' width={100} /> : `${integration.nickname}`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        <AffiliateTracking
          platform={TUNE}
          uid={firebase.auth().currentUser.uid}
          integration_id={id}
          webhookUrlBase={WEBHOOK_URL_BASE}
          connectorUrlSuffix={'postback'}
          connectorUrlParams={generatePostbackParams(TUNE)}
          urlLabel={'Postback URL'}
          isLoadingConnectors={isLoadingConnectors}
          connectors={connectors}
          setMessage={setMessage}
          setShowMessage={setShowMessage}
          copyToClipboard={copyToClipboard}
          instructionsNode={
            <Box>
              <Typography variant='h6' paragraph>
                Global Postback Setup Instructions (strongly recommended)
              </Typography>
              <Typography variant='body2' paragraph>
                The recommended setup is to create a single <b>Global Postback</b> in your TUNE account that applies to all offers.
                This way, you only need to create the postback once, and any new offers you add in the future will automatically have the postback applied.
                In some cases, you may not have permissions to do this yourself, in which case you will need to provide these instructions to the Advertiser that
                manages your TUNE affiliate account.
              </Typography>
              <Typography variant='body2' paragraph>
                To add a global postback, login to TUNE, open <b>Tools</b> in the left menu, then select <b>Pixels/Postbacks</b>. Click <b>Add New Pixel/Postback</b>.
                For <b>Code Type</b>, select <b>Postback URL</b>. For <b>Code</b> copy the URL for the Contact Connector below and paste it in the box. Click <b>Create</b>.
              </Typography>
              <Typography variant='body2' paragraph>
                <b>NOTE: if you have multiple Contact Connectors, perform this once for each Contact Connector.</b> If you are provided instructions to an Advertiser,
                provide them with the URL for each Contact Connector and tell them to follow the instructions above for each URL.
              </Typography>
              <Typography variant='h6' paragraph>
                Offer Level Postback Setup Instructions (only if necessary)
              </Typography>
              <Typography variant='body2' paragraph>
                This is NOT our recommended setup, because if forces you to create a postback for each offer in your TUNE account, and remember to add new Postbacks
                if you have new offers in the future. With that said, there can be meaningful reasons to do this, so we are providing instructions here in case you need them.
              </Typography>
              <Typography variant='body2' paragraph>
                You will need to create one <b>Postback</b> in your TUNE account for each offer, and for each Contact Connector below.
                To do this, login to TUNE for the same account that you connected to created this Data Source.
                Then in the left menu, select <b>Offers</b>. When recommend only adding this to Live Offers, but you can add it to all offers
                if you want these to have the postback in place just in case. Click on <b>My Live Offers</b> or <b>All Offers</b> based on your choice.
              </Typography>
              <Typography variant='body2' paragraph>
                Once you are seeing your list of offers, you will need to perform the following steps <b>for each offer and for each Contact Connector below.</b>
              </Typography>
              <Typography variant='body2' paragraph>
                Click on an offer. Scroll down to <b>Your Pixels / Postbacks</b>. Click to expand the section, then click <b>Add New Pixel/Postback</b>.
                For <b>Code Type</b>, select <b>Postback URL</b>. For <b>Code</b> copy the URL for the Contact Connector below and paste it in the box. Click <b>Create</b>.
              </Typography>
              <Typography variant='body2' paragraph>
                <b>NOTE: if you have multiple Contact Connectors, perform this once for each Contact Connector.</b>
              </Typography>
            </Box>
          }
        />

        {/* Delete Source */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Delete
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='body2' gutterBottom>
                    If you want to remove this data integration so transacations can no longer be saved, click the button below.
                  </Typography>
                  <Button
                    className={classes.redButton}
                    variant='text'
                    onClick={() => setShowRevokeConfirmation(true)}
                  >
                    Delete Source
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      </Box>
      <Dialog
        open={showRevokeConfirmation}
        onClose={() => setShowRevokeConfirmation(false)}
      >
        <DialogTitle>
          Remove access to account <b>&quot;{integration.nickname}&quot;</b>?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will prevent data from being synced to LTV Numbers.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            className={classes.redButton}
            variant='text'
            onClick={() => setShowRevokeConfirmation(false)}
          >
            Cancel
          </Button>
          <Button
            color='primary'
            onClick={() => revoke()}
          >
            {isLoadingRevoke ? <CircularProgress color='inherit' size={24} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        autoHideDuration={3000}
        onClose={handleCloseMessage}
      >
        <Alert
          severity={messageSeverity}
          variant='filled'
          onClose={handleCloseMessage}
        >
          {message}
        </Alert>
      </Snackbar>
    </div>
  )
}

TuneSource.propTypes = {}

export default TuneSource